import { useCallback, useEffect, useMemo, useState } from 'react'

import type { ComicPanelAspectPreset } from '../../../lib/comicProjectSettings'
import {
  buildReaderEpisodeSummaries,
  type ComicReaderPageEntry,
  type ComicReaderViewMode,
  filterReaderEntries,
  firstEntryIndexForEpisode,
} from '../../../lib/comicReaderModel'
import type { ComicEpisode } from '../../../lib/comicTypes'
import { chromeToggleChipClass } from '../../ChromeSurface'
import { ComicReaderModeToggle } from './ComicReaderModeToggle'
import { ComicReaderPaginateView } from './ComicReaderPaginateView'
import { ComicReaderScrollView } from './ComicReaderScrollView'
import {
  ComicReaderTocModal,
  ComicReaderTocSidebar,
  ComicReaderTocToolbarButton,
} from './ComicReaderToc'

type Props = {
  angular: boolean
  entries: ComicReaderPageEntry[]
  episodesById: Map<string, ComicEpisode>
  panelAspect: ComicPanelAspectPreset
  /** 仅条漫版式（单话连续阅读） */
  webtoonOnly?: boolean
  initialMode?: ComicReaderViewMode
  initialPageKey?: string | null
  onNavigatePage?: (episodeId: string, pageId: string, panelId?: string) => void
}

export function ComicReaderShell({
  angular,
  entries,
  episodesById,
  panelAspect,
  webtoonOnly = false,
  initialMode = 'scroll',
  initialPageKey = null,
  onNavigatePage,
}: Props) {
  const [mode, setMode] = useState<ComicReaderViewMode>(initialMode)
  const [onlyWithContent, setOnlyWithContent] = useState(false)
  const [pageIndex, setPageIndex] = useState(0)
  const [scrollToKey, setScrollToKey] = useState<string | null>(initialPageKey)
  const [tocModalOpen, setTocModalOpen] = useState(false)

  const visibleEntries = useMemo(
    () => filterReaderEntries(entries, episodesById, { onlyWithContent, webtoonOnly }),
    [entries, episodesById, onlyWithContent, webtoonOnly],
  )

  const episodeSummaries = useMemo(
    () => buildReaderEpisodeSummaries(visibleEntries),
    [visibleEntries],
  )

  const activeEpisodeId =
    visibleEntries[pageIndex]?.episodeId ?? episodeSummaries[0]?.episodeId ?? null
  const showEpisodeToc = episodeSummaries.length > 0
  const showSidebarToc = episodeSummaries.length > 1

  useEffect(() => {
    if (!initialPageKey) return
    const idx = visibleEntries.findIndex((e) => e.key === initialPageKey)
    if (idx >= 0) setPageIndex(idx)
  }, [initialPageKey, visibleEntries])

  useEffect(() => {
    if (pageIndex >= visibleEntries.length) {
      setPageIndex(Math.max(0, visibleEntries.length - 1))
    }
  }, [pageIndex, visibleEntries.length])

  const jumpToEpisode = useCallback(
    (episodeId: string) => {
      const idx = firstEntryIndexForEpisode(visibleEntries, episodeId)
      if (idx < 0) return
      setPageIndex(idx)
      setScrollToKey(visibleEntries[idx]?.key ?? null)
    },
    [visibleEntries],
  )

  const handlePageIndexChange = useCallback(
    (idx: number) => {
      setPageIndex(idx)
      setScrollToKey(visibleEntries[idx]?.key ?? null)
    },
    [visibleEntries],
  )

  if (entries.length === 0) {
    return (
      <p className="typo-lib-empty flex flex-1 items-center justify-center py-16 text-center text-ink-soft">
        本作品暂无可预览的页面
      </p>
    )
  }

  if (visibleEntries.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-center">
        <p className="text-sm text-ink-soft">当前筛选下没有已成图的页面</p>
        <button
          type="button"
          className={chromeToggleChipClass(angular, false)}
          onClick={() => setOnlyWithContent(false)}
        >
          显示全部页面
        </button>
      </div>
    )
  }

  const tocProps = {
    angular,
    entries: visibleEntries,
    activeEpisodeId,
    onSelectEpisode: jumpToEpisode,
  }

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      {showSidebarToc ? <ComicReaderTocSidebar {...tocProps} /> : null}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-line px-3 py-2.5 sm:px-5">
          <div className="flex min-w-0 items-center gap-2">
            <ComicReaderModeToggle angular={angular} mode={mode} onChange={setMode} />
            {showEpisodeToc ? (
              <ComicReaderTocToolbarButton
                angular={angular}
                onOpenToc={() => setTocModalOpen(true)}
              />
            ) : null}
          </div>
          <button
            type="button"
            className={chromeToggleChipClass(angular, onlyWithContent)}
            onClick={() => setOnlyWithContent((v) => !v)}
            aria-pressed={onlyWithContent}
          >
            仅已成图
          </button>
        </div>

        {mode === 'scroll' ? (
          <ComicReaderScrollView
            angular={angular}
            entries={visibleEntries}
            episodesById={episodesById}
            panelAspect={panelAspect}
            scrollToKey={scrollToKey}
            onNavigatePage={onNavigatePage}
          />
        ) : (
          <ComicReaderPaginateView
            angular={angular}
            entries={visibleEntries}
            episodesById={episodesById}
            panelAspect={panelAspect}
            pageIndex={pageIndex}
            onPageIndexChange={handlePageIndexChange}
            onNavigatePage={onNavigatePage}
          />
        )}
      </div>

      {showEpisodeToc ? (
        <ComicReaderTocModal
          {...tocProps}
          open={tocModalOpen}
          onClose={() => setTocModalOpen(false)}
        />
      ) : null}
    </div>
  )
}
